import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Tag, Copy, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { api, money, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const EMPTY = { code: "", type: "percent", value: 10, active: true, expires_at: null, max_uses: null, min_total: 0, tour_ids: [], description: "" };
const F = ({ label, hint, children, className = "" }) => (
  <div className={className}><label className="text-sm font-medium text-gray-700">{label}</label><div className="mt-1.5">{children}</div>{hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}</div>
);

const statusOf = (c) => {
  const today = new Date().toISOString().slice(0, 10);
  if (!c.active) return ["Inactivo", "bg-gray-100 text-gray-600"];
  if (c.expires_at && today > c.expires_at) return ["Expirado", "bg-red-100 text-red-700"];
  if (c.max_uses && c.uses >= c.max_uses) return ["Agotado", "bg-amber-100 text-amber-800"];
  return ["Activo", "bg-emerald-100 text-emerald-700"];
};

export default function CouponsAdmin() {
  const [list, setList] = useState(null);
  const [tours, setTours] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);

  const load = () => api.get("/admin/coupons").then((r) => setList(r.data));
  useEffect(() => { load(); api.get("/admin/tours").then((r) => setTours(r.data)); }, []);
  const set = (k, v) => setEdit((e) => ({ ...e, [k]: v }));

  const save = async () => {
    setSaving(true);
    const body = { ...edit, value: Number(edit.value), min_total: Number(edit.min_total) || 0, max_uses: edit.max_uses ? Number(edit.max_uses) : null };
    try {
      if (edit.id) await api.put(`/admin/coupons/${edit.id}`, body); else await api.post("/admin/coupons", body);
      toast.success("Cupón guardado"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const toggle = async (c) => { await api.put(`/admin/coupons/${c.id}`, { ...EMPTY, ...c, active: !c.active }); load(); };
  const remove = async () => { await api.delete(`/admin/coupons/${del.id}`); setDel(null); load(); toast.success("Cupón eliminado"); };
  const genCode = () => set("code", `RDX${Math.random().toString(36).slice(2, 7).toUpperCase()}`);

  if (!list) return <Loader2 className="w-6 h-6 animate-spin brand-text" />;

  return (
    <div className="space-y-4" data-testid="admin-coupons">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{list.length} cupones · Los clientes escriben el código en el carrito antes de pagar.</p>
        <button onClick={() => setEdit({ ...EMPTY })} className="btn-brand px-4 py-2 rounded-md font-medium flex items-center gap-2" data-testid="add-coupon-btn"><Plus className="w-4 h-4" />Nuevo cupón</button>
      </div>
      {list.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
          <Tag className="w-10 h-10 mx-auto text-gray-300" /><p className="text-gray-500 mt-3">Aún no tienes cupones. Crea el primero para atraer más clientes.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {list.map((c) => {
            const [st, cls] = statusOf(c);
            return (
              <div key={c.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 relative overflow-hidden" data-testid="coupon-card">
                <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full bg-[var(--accent-c)]/15" />
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <button onClick={() => { navigator.clipboard?.writeText(c.code); toast.success("Código copiado"); }} className="font-mono font-bold text-lg tracking-wider text-gray-900 flex items-center gap-1.5 hover:text-[var(--brand)]" title="Copiar">
                      {c.code}<Copy className="w-3.5 h-3.5 opacity-50" />
                    </button>
                    <div className="text-2xl font-bold brand-text mt-1">{c.type === "percent" ? `${c.value}%` : money(c.value)} <span className="text-sm font-normal text-gray-500">de descuento</span></div>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${cls}`}>{st}</span>
                </div>
                {c.description && <p className="text-sm text-gray-600 mt-2">{c.description}</p>}
                <div className="text-xs text-gray-500 mt-3 space-y-0.5">
                  <div>Usos: <b className="text-gray-700">{c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</b></div>
                  {c.expires_at && <div>Vence: {format(parseISO(c.expires_at), "d MMM yyyy", { locale: es })}</div>}
                  {c.min_total > 0 && <div>Compra mínima: {money(c.min_total)}</div>}
                  <div>{c.tour_ids?.length ? `Aplica a ${c.tour_ids.length} excursión(es)` : "Aplica a todas las excursiones"}</div>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <label className="flex items-center gap-2 text-sm mr-auto"><Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid="coupon-active-switch" />{c.active ? "Activo" : "Inactivo"}</label>
                  <button onClick={() => setEdit({ ...EMPTY, ...c })} className="h-9 w-9 rounded-md border border-gray-200 hover:border-[var(--brand)] hover:text-[var(--brand)] flex items-center justify-center" data-testid="edit-coupon-btn"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => setDel(c)} className="h-9 w-9 rounded-md border border-red-100 text-red-500 hover:bg-red-50 flex items-center justify-center" data-testid="delete-coupon-btn"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {edit && (<>
            <DialogHeader><DialogTitle>{edit.id ? "Editar cupón" : "Nuevo cupón"}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Código *" hint="Solo letras, números, - y _">
                <div className="flex gap-2"><Input value={edit.code} onChange={(e) => set("code", e.target.value.toUpperCase())} className="font-mono uppercase tracking-wider" data-testid="coupon-code-input" />
                  <button type="button" onClick={genCode} className="px-3 rounded-md border text-sm hover:bg-gray-50 shrink-0">Generar</button></div>
              </F>
              <F label="Tipo de descuento">
                <Select value={edit.type} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger data-testid="coupon-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">Porcentaje (%)</SelectItem><SelectItem value="fixed">Monto fijo (USD)</SelectItem></SelectContent>
                </Select>
              </F>
              <F label={edit.type === "percent" ? "Descuento (%) *" : "Descuento (USD) *"}><Input type="number" min="0" step="0.01" value={edit.value} onChange={(e) => set("value", e.target.value)} data-testid="coupon-value-input" /></F>
              <F label="Compra mínima (USD)"><Input type="number" min="0" value={edit.min_total} onChange={(e) => set("min_total", e.target.value)} /></F>
              <F label="Límite de usos" hint="Vacío = ilimitado"><Input type="number" min="1" value={edit.max_uses ?? ""} onChange={(e) => set("max_uses", e.target.value || null)} data-testid="coupon-maxuses-input" /></F>
              <F label="Fecha de vencimiento" hint="Vacío = sin vencimiento">
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="flex-1 h-10 rounded-md border border-input px-3 flex items-center gap-2 text-sm text-left" data-testid="coupon-expiry-btn">
                        <CalendarDays className="w-4 h-4 brand-text" />{edit.expires_at ? format(parseISO(edit.expires_at), "d MMM yyyy", { locale: es }) : <span className="text-gray-400">Sin vencimiento</span>}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" locale={es} selected={edit.expires_at ? parseISO(edit.expires_at) : undefined} onSelect={(d) => set("expires_at", d ? format(d, "yyyy-MM-dd") : null)} initialFocus />
                    </PopoverContent>
                  </Popover>
                  {edit.expires_at && <button type="button" onClick={() => set("expires_at", null)} className="px-3 rounded-md border text-sm hover:bg-gray-50">Quitar</button>}
                </div>
              </F>
              <F label="Descripción (visible al cliente)" className="sm:col-span-2"><Input value={edit.description} onChange={(e) => set("description", e.target.value)} placeholder="Ej: 10% de descuento de bienvenida" /></F>
              <F label="Aplica a" hint="Si no marcas ninguna, aplica a todas las excursiones" className="sm:col-span-2">
                <div className="grid sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-md p-3">
                  {tours.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={edit.tour_ids.includes(t.id)} onCheckedChange={(c) => set("tour_ids", c ? [...edit.tour_ids, t.id] : edit.tour_ids.filter((x) => x !== t.id))} />
                      <span className="truncate">{t.title}</span>
                    </label>
                  ))}
                </div>
              </F>
              <label className="flex items-center gap-2 text-sm"><Switch checked={edit.active} onCheckedChange={(v) => set("active", v)} />Cupón activo</label>
            </div>
            <DialogFooter>
              <button onClick={() => setEdit(null)} className="px-4 py-2 rounded-md border hover:bg-gray-50">Cancelar</button>
              <button onClick={save} disabled={saving || !edit.code || !edit.value} className="btn-brand px-5 py-2 rounded-md font-medium flex items-center gap-2 disabled:opacity-60" data-testid="save-coupon-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />}Guardar</button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar el cupón {del?.code}?</AlertDialogTitle><AlertDialogDescription>Los clientes ya no podrán usarlo.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-coupon-btn">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
